// app/(app)/admin/verification/page.jsx
'use client';

import React, { useCallback, useEffect, useState } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  doc,
  updateDoc,
  setDoc,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@root/firebase';
import Layout from '@components/Layout';
import { useAuth } from '@context/AuthContext';
import { toast } from 'react-hot-toast';

function Guard({ children }) {
  const { globalData } = useAuth();
  const isAdmin = globalData?.isAdmin === true;

  if (!isAdmin) {
    return (
      <Layout>
        <div className='container-fluid'>
          <div className='alert alert-danger mt-3'>
            You don’t have permission to view this page.
          </div>
        </div>
      </Layout>
    );
  }
  return children;
}

function EvidenceRow({ productId, ev, onChange }) {
  const setStatus = async (status) => {
    try {
      await updateDoc(doc(db, `marketplace/${productId}/evidence/${ev.id}`), {
        status,
        updatedAt: Timestamp.now(),
      });
      onChange?.();
      toast.success(`Evidence ${status}`);
    } catch (e) {
      console.error(e);
      toast.error('Failed to update evidence status');
    }
  };

  return (
    <tr>
      <td className='small'>{ev.type || '-'}</td>
      <td
        className='small'
        style={{
          maxWidth: 360,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={ev.value}
      >
        <a href={ev.value} target='_blank' rel='noreferrer'>
          {ev.value}
        </a>
      </td>
      <td className='small'>{(ev.status || 'pending').toUpperCase()}</td>
      <td className='text-end'>
        <button
          className='btn btn-sm btn-outline-success me-2'
          onClick={() => setStatus('approved')}
        >
          Approve
        </button>
        <button
          className='btn btn-sm btn-outline-danger'
          onClick={() => setStatus('rejected')}
        >
          Reject
        </button>
      </td>
    </tr>
  );
}

function ProductCardAdmin({ product, reloadList }) {
  const [evidence, setEvidence] = useState([]);
  const [loadingEv, setLoadingEv] = useState(false);

  const loadEvidence = useCallback(async () => {
    setLoadingEv(true);
    try {
      const snap = await getDocs(
        collection(db, `marketplace/${product.id}/evidence`)
      );
      setEvidence(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } finally {
      setLoadingEv(false);
    }
  }, [product.id]);

  useEffect(() => {
    loadEvidence();
  }, [loadEvidence]);

  const finalize = async (status) => {
    try {
      // simple score: % approved
      const total = evidence.length || 1;
      const approved = evidence.filter((e) => e.status === 'approved').length;
      const score = Math.round((approved / total) * 100);

      // write verification detail doc
      await setDoc(
        doc(db, `marketplace/${product.id}/verification/status`),
        {
          status,
          reviewerUid: 'ADMIN_UID', // replace with your admin uid if you track it
          reviewedAt: Timestamp.now(),
          notes: '',
          score,
        },
        { merge: true }
      );

      // mirror to root
      await updateDoc(doc(db, 'marketplace', product.id), {
        verificationStatus: status,
        verificationScore: score,
        updatedAt: Timestamp.now(),
      });

      toast.success(`Product ${status}`);
      reloadList?.();
    } catch (e) {
      console.error(e);
      toast.error('Failed to finalize product');
    }
  };

  // derive a display status if missing on the root:
  const displayStatus = (product.verificationStatus || 'none').toUpperCase();

  return (
    <div className='card mb-3'>
      <div className='card-header d-flex align-items-center justify-content-between'>
        <div className='d-flex align-items-center gap-3'>
          {product.avatar ? (
            <img
              src={product.avatar}
              alt=''
              style={{
                width: 44,
                height: 44,
                objectFit: 'cover',
                borderRadius: 6,
              }}
            />
          ) : (
            <div
              style={{
                width: 44,
                height: 44,
                background: '#eee',
                borderRadius: 6,
              }}
            />
          )}
          <div>
            <div className='fw-bold'>{product.product}</div>
            <div className='small text-muted'>{product.brand || '-'}</div>
          </div>
        </div>
        <div className='d-flex align-items-center gap-2'>
          <span className='badge bg-light text-dark'>{displayStatus}</span>
          <button
            className='btn btn-sm btn-success'
            onClick={() => finalize('verified')}
          >
            Mark Verified
          </button>
          <button
            className='btn btn-sm btn-danger'
            onClick={() => finalize('rejected')}
          >
            Reject Product
          </button>
        </div>
      </div>

      <div className='card-body p-0'>
        <div className='table-responsive'>
          <table className='table table-sm mb-0'>
            <thead>
              <tr>
                <th style={{ width: '22%' }}>Type</th>
                <th>Link</th>
                <th style={{ width: '12%' }}>Status</th>
                <th style={{ width: '20%' }} className='text-end'>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {loadingEv ? (
                <tr>
                  <td colSpan={4} className='p-3'>
                    Loading evidence…
                  </td>
                </tr>
              ) : evidence.length === 0 ? (
                <tr>
                  <td colSpan={4} className='p-3 text-muted'>
                    No evidence submitted.
                  </td>
                </tr>
              ) : (
                evidence.map((ev) => (
                  <EvidenceRow
                    key={ev.id}
                    productId={product.id}
                    ev={ev}
                    onChange={loadEvidence}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function VerificationAdminPage() {
  // 'all' adds all products, regardless of status (even if the field is missing)
  const [filter, setFilter] = useState('all'); // all | pending | verified | rejected | none
  const [qText, setQText] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const base = [
        where('type', '==', 'products'),
        orderBy('updatedAt', 'desc'),
        limit(100),
      ];

      // Only apply status filter if we are NOT on "all"
      const qParts =
        filter === 'all'
          ? base
          : [where('verificationStatus', '==', filter), ...base];

      const snap = await getDocs(
        query(collection(db, 'marketplace'), ...qParts)
      );
      let items = snap.docs.map((d) => {
        const data = d.data() || {};
        // If no explicit verificationStatus, treat as 'none' (Unverified)
        const verificationStatus = data.verificationStatus || 'none';
        return { id: d.id, ...data, verificationStatus };
      });

      // client-side search
      const term = qText.trim().toLowerCase();
      if (term) {
        items = items.filter(
          (it) =>
            it.product?.toLowerCase().includes(term) ||
            it.brand?.toLowerCase().includes(term)
        );
      }

      setRows(items);
    } finally {
      setLoading(false);
    }
  }, [filter, qText]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Guard>
      <Layout>
        <div className='container-fluid'>
          <div className='d-flex align-items-center justify-content-between mb-3'>
            <h4 className='mb-0'>Product Verification</h4>
            <div className='d-flex align-items-center gap-2 form-floating'>
              <input
                className='form-control form-control-sm'
                style={{ width: 260 }}
                placeholder='Search product or brand'
                value={qText}
                onChange={(e) => setQText(e.target.value)}
              />
              <select
                className='form-select form-select-sm native-select'
                style={{ width: 200 }}
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              >
                <option value='all'>All</option>
                <option value='pending'>Pending</option>
                <option value='verified'>Verified</option>
                <option value='rejected'>Rejected</option>
                <option value='none'>Unverified</option>
              </select>
              <button className='btn btn-sm btn-outline-primary' onClick={load}>
                Refresh
              </button>
            </div>
          </div>

          {loading ? (
            <div className='alert alert-light'>Loading…</div>
          ) : rows.length === 0 ? (
            <div className='alert alert-light'>
              No {filter === 'all' ? '' : filter + ' '}products
              {qText ? ` matching “${qText}”` : ''}.
            </div>
          ) : (
            rows.map((p) => (
              <ProductCardAdmin key={p.id} product={p} reloadList={load} />
            ))
          )}
        </div>
      </Layout>
    </Guard>
  );
}
